import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabase';
import { getActiveChatKey, subscribeActiveChatKey } from '@/utils/activeChatKey';
import { logger } from '@/utils/logger';
import {
  PRESENCE_HEARTBEAT_MS,
  getDeviceId,
  presenceRow,
  shouldWritePresence,
  type PresenceRow,
} from '@/utils/presence';

/**
 * Interactions that count as "still here". Passive listeners on the window, so they add
 * nothing to scroll or input latency; the sync they trigger writes nothing unless the
 * state actually changed or the heartbeat is due.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

/**
 * Keeps this device's row in public.chat_presence current, so notify_chat_message() can
 * tell a person who is reading a thread from one who is not.
 *
 * Registered once for the whole app (PresenceRegistrar in App.tsx), not per chat: the
 * question the trigger asks is about the whole client, and the thread on screen arrives
 * through the activeChatKey store.
 */
export function usePresenceHeartbeat(userId: string | undefined) {
  useEffect(() => {
    if (!userId) return;

    const deviceId = getDeviceId();
    let lastRow: PresenceRow | null = null;
    let lastWriteAt = 0;
    let lastActivityAt = Date.now();
    let visible = document.visibilityState === 'visible';
    let stopped = false;

    const write = (row: PresenceRow) => {
      // Recorded before the await so a burst of events cannot queue a burst of writes.
      lastRow = row;
      lastWriteAt = Date.now();

      void supabase
        .from('chat_presence')
        .upsert(
          {
            user_id: userId,
            device_id: deviceId,
            app_active: row.app_active,
            active_chat_key: row.active_chat_key,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,device_id' }
        )
        .then(({ error }) => {
          if (!error) return;
          logger.error('usePresenceHeartbeat upsert failed', error);
          // Forget the optimistic record so the next tick retries rather than assuming
          // the server knows where we are. A stale row only ever costs an extra push.
          lastRow = null;
        });
    };

    const sync = () => {
      if (stopped) return;
      const now = Date.now();
      const next = presenceRow({
        visible,
        activeChatKey: getActiveChatKey(),
        lastActivityAt,
        now,
      });
      if (!shouldWritePresence(lastRow, next, lastWriteAt, now)) return;
      write(next);
    };

    const noteActivity = () => {
      lastActivityAt = Date.now();
      sync();
    };

    const onVisibilityChange = () => {
      visible = document.visibilityState === 'visible';
      // Coming back counts as activity: the person is demonstrably at the screen.
      if (visible) lastActivityAt = Date.now();
      sync();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, noteActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    const unsubscribeActiveChat = subscribeActiveChatKey(sync);

    // Backgrounding is announced rather than waited out: the TTL is the fallback for
    // crashes and lost networks, not the normal path.
    const appStateListener = Capacitor.isNativePlatform()
      ? CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          visible = isActive;
          if (isActive) lastActivityAt = Date.now();
          sync();
        })
      : null;

    // Best effort on web: a closing tab often gets one more request out.
    const onPageHide = () => write({ app_active: false, active_chat_key: null });
    window.addEventListener('pagehide', onPageHide);

    sync();
    const heartbeat = window.setInterval(sync, PRESENCE_HEARTBEAT_MS);

    return () => {
      stopped = true;
      window.clearInterval(heartbeat);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, noteActivity);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      unsubscribeActiveChat();
      void appStateListener?.then((listener) => listener.remove());

      // Signing out or unmounting means this device is no longer anywhere. Written
      // directly rather than through sync(), which no longer runs.
      write({ app_active: false, active_chat_key: null });
    };
  }, [userId]);
}
